import { z } from 'zod';
import type { IndicadoresEstoque, IndicadoresVendas, ValorComparado } from './indicadores';

export const indicadorSchema = z.object({
  chave: z.string(),
  titulo: z.string(),
  valor: z.number().nullable(),
  formato: z.enum(['moeda', 'numero', 'dias', 'percentual']),
  bloqueado: z.boolean(),
});
export type Indicador = z.infer<typeof indicadorSchema> & {
  /** comparação com o período anterior */
  anterior?: number | null;
  variacao?: number | null;
  /** quando subir é ruim (ex.: dias parado) */
  inverso?: boolean;
  /** rota do detalhe ao clicar */
  detalhe?: string;
};

/** Seções que só aparecem completas no Elite (nos outros planos, prévia desfocada com dados reais). */
export const SECOES_COMPLETAS = [
  'giro',
  'preco_fipe',
  'preparacao_etapa',
  'custo_preparacao',
  'modelos_rapidos',
  'comparativo_filiais',
] as const;
export type SecaoCompleta = (typeof SECOES_COMPLETAS)[number];

export interface ResumoPainel {
  nivel: 'resumido' | 'completo';
  periodo: { de: string; ate: string };
  indicadores: Indicador[];
  estoque: IndicadoresEstoque;
  vendas: IndicadoresVendas;
  bloqueados: SecaoCompleta[];
  alertas: AlertaEstoque[];
  historico: { eventosRegistrados: number; desde: string | null };
  recalculo: {
    status: 'na_fila' | 'processando' | 'concluido' | 'falhou';
    progresso: number;
  } | null;
}
export type { ValorComparado };

export interface AlertaEstoque {
  veiculoId: string;
  placa: string;
  descricao: string;
  dias: number;
  /** 30, 60, 90 = 1º, 2º e 3º nível de alerta (os dias de cada nível a loja escolhe) */
  nivel: 30 | 60 | 90;
  /** dias do limite que passou */
  limite?: number;
}

// ─────────── filtros e personalização do Painel do Dono ───────────

/** Filtros extras do Painel (além do período e da filial do topo). Vazio = todos. */
export const filtrosPainelSchema = z.object({
  vendedor: z.string().uuid().optional().catch(undefined),
  marca: z.string().trim().min(1).max(60).optional().catch(undefined),
});
export type FiltrosPainel = z.infer<typeof filtrosPainelSchema>;

/** Blocos da aba Resumo que o dono escolhe mostrar e em que ordem. */
export const BLOCOS_RESUMO = [
  'atencao',
  'indicadores',
  'saude',
  'vendas_mes',
  'tempo_estoque',
  'parados',
  'modelos',
] as const;
export type BlocoResumo = (typeof BLOCOS_RESUMO)[number];
export const NOME_BLOCO_RESUMO: Record<BlocoResumo, string> = {
  atencao: 'Pendências',
  indicadores: 'Números principais',
  saude: 'Saúde da loja',
  vendas_mes: 'Vendas e lucro por mês',
  tempo_estoque: 'Tempo em estoque',
  parados: 'Carros parados há muito tempo',
  modelos: 'Modelos que giram mais rápido',
};

/** Números que podem aparecer em "Números principais" (até 8). */
export const KPIS_PAINEL = [
  'lucro',
  'vendidos',
  'faturamento',
  'margem',
  'ticket',
  'estoque',
  'valor_estoque',
  'capital',
  'dias_parado',
  'giro',
  'caixa',
  'leads',
  'conversao',
  'sem_resposta',
] as const;
export type KpiPainel = (typeof KPIS_PAINEL)[number];

/** O que cada número quer dizer, em linguagem de lojista (definições exatas em docs/METRICAS.md). */
export const EXPLICACAO_KPI: Record<KpiPainel, string> = {
  lucro: 'Quanto sobrou das vendas do período: valor de venda menos tudo o que cada carro custou.',
  vendidos: 'Quantos carros foram vendidos no período.',
  faturamento: 'Soma do valor de todas as vendas do período.',
  margem: 'De cada R$ 100 vendidos, quantos reais viraram lucro.',
  ticket: 'Valor médio de cada venda.',
  estoque: 'Carros na loja agora, à venda ou em preparação.',
  valor_estoque: 'Quanto o estoque vale pelos preços anunciados.',
  capital:
    'O dinheiro que você colocou nos carros que ainda estão na loja (compra mais preparação).',
  dias_parado: 'Há quantos dias, em média, os carros estão na loja.',
  giro: 'Quantas vezes o estoque "virou" no período. Quanto maior, mais rápido os carros vendem.',
  caixa: 'Dinheiro disponível hoje somando todas as contas da loja.',
  leads: 'Quantas pessoas interessadas chegaram no período (site, portais, WhatsApp e loja).',
  conversao: 'De cada 100 interessados, quantos compraram.',
  sem_resposta: 'Interessados esperando o primeiro contato. Quem responde primeiro costuma vender.',
};
export const KPIS_PADRAO: KpiPainel[] = [
  'lucro',
  'vendidos',
  'faturamento',
  'margem',
  'estoque',
  'capital',
  'dias_parado',
  'giro',
];
export const BLOCOS_PADRAO: BlocoResumo[] = [
  'atencao',
  'indicadores',
  'vendas_mes',
  'tempo_estoque',
  'saude',
  'parados',
  'modelos',
];

export const preferenciasPainelSchema = z.object({
  blocos: z.array(z.enum(BLOCOS_RESUMO)).max(BLOCOS_RESUMO.length),
  kpis: z.array(z.enum(KPIS_PAINEL)).min(1).max(8),
});
export type PreferenciasPainel = z.infer<typeof preferenciasPainelSchema>;

/** Lê o que veio do banco; qualquer coisa inválida volta ao padrão. */
export function preferenciasPainelOuPadrao(v: unknown): PreferenciasPainel {
  const r = preferenciasPainelSchema.safeParse(v);
  return r.success
    ? { blocos: [...new Set(r.data.blocos)], kpis: [...new Set(r.data.kpis)] }
    : { blocos: BLOCOS_PADRAO, kpis: KPIS_PADRAO };
}
